import { accountKeys, prepaidRecognitionKeys, type SchedulePrepaidRecognitionInput } from '@taler/api-client'
import type { Locale } from '@taler/domain'
import { useMutation, useQuery, useQueryClient } from '@tanstack/vue-query'
import { computed, type ComputedRef } from 'vue'
import { useApi } from '@/app/api'
import { retrying } from '@/app/retrying'
import { type ExpenseAccountOption, expenseAccountOptions } from '../internal/expense-accounts.ts'
import { isIdempotencyPending, isScheduleNotFound, problemDetail } from '../internal/problem.ts'
import type { PrepaidSchedule } from '../internal/types.ts'

export interface UsePrepaidScheduleOptions {
  readonly ledgerId: () => string | undefined
  readonly originEntryId: () => string | undefined
  readonly enabled: () => boolean
  readonly locale: () => Locale
}

export interface UsePrepaidSchedule {
  /** `undefined` once loaded and `notScheduled` is true: no schedule exists for this entry yet. */
  readonly schedule: ComputedRef<PrepaidSchedule | undefined>
  readonly notScheduled: ComputedRef<boolean>
  readonly isLoading: ComputedRef<boolean>
  readonly error: ComputedRef<Error | null>
  readonly retrying: ComputedRef<boolean>
  readonly expenseAccountOptions: ComputedRef<readonly ExpenseAccountOption[]>
  readonly isScheduling: ComputedRef<boolean>
  readonly scheduleErrorDetail: ComputedRef<string | undefined>
  readonly scheduleErrorPending: ComputedRef<boolean>
  scheduleRecognition(input: Omit<SchedulePrepaidRecognitionInput, 'originEntryId'>): Promise<void>
  retry(): void
}

export function usePrepaidSchedule(options: UsePrepaidScheduleOptions): UsePrepaidSchedule {
  const api = useApi()
  const queryClient = useQueryClient()
  const active = computed(
    () => options.enabled() && options.ledgerId() !== undefined && options.originEntryId() !== undefined,
  )

  const scheduleQuery = useQuery({
    queryKey: computed(() =>
      prepaidRecognitionKeys.schedule(options.ledgerId() ?? '', options.originEntryId() ?? ''),
    ),
    queryFn: ({ signal }) =>
      api.prepaidRecognitions.getSchedule(options.ledgerId()!, options.originEntryId()!, signal),
    enabled: active,
    retry: false,
  })

  const notScheduled = computed(
    () =>
      isScheduleNotFound(scheduleQuery.error.value) ||
      (scheduleQuery.data.value !== undefined && scheduleQuery.data.value.recognitions.length === 0),
  )

  const accountsTreeQuery = useQuery({
    queryKey: computed(() => accountKeys.tree(options.ledgerId() ?? '')),
    queryFn: ({ signal }) => api.accounts.tree(options.ledgerId()!, signal),
    enabled: active,
    retry: false,
  })

  function invalidate(): Promise<void> {
    const ledgerId = options.ledgerId()
    const originEntryId = options.originEntryId()
    if (ledgerId === undefined || originEntryId === undefined) return Promise.resolve()
    return Promise.all(
      prepaidRecognitionKeys
        .invalidatedByWrite(ledgerId, originEntryId)
        .map((queryKey) => queryClient.invalidateQueries({ queryKey })),
    ).then(() => undefined)
  }

  const scheduleMutation = useMutation({
    mutationFn: (input: Omit<SchedulePrepaidRecognitionInput, 'originEntryId'>) =>
      api.prepaidRecognitions.scheduleRecognition(options.ledgerId()!, {
        ...input,
        originEntryId: options.originEntryId()!,
      }),
    onSuccess: () => invalidate(),
  })

  return {
    schedule: computed(() => (notScheduled.value ? undefined : scheduleQuery.data.value)),
    notScheduled,
    isLoading: computed(() => active.value && scheduleQuery.isPending.value),
    error: computed(() => (notScheduled.value ? null : scheduleQuery.error.value)),
    retrying: retrying(scheduleQuery),
    expenseAccountOptions: computed(() =>
      expenseAccountOptions(accountsTreeQuery.data.value ?? [], options.locale()),
    ),
    isScheduling: computed(() => scheduleMutation.isPending.value),
    scheduleErrorDetail: computed(() => problemDetail(scheduleMutation.error.value)),
    scheduleErrorPending: computed(() => isIdempotencyPending(scheduleMutation.error.value)),
    scheduleRecognition: async (input) => {
      await scheduleMutation.mutateAsync(input)
    },
    retry: () => {
      void scheduleQuery.refetch()
      void accountsTreeQuery.refetch()
    },
  }
}
